import {
  getSnapshot as getQzSnapshot,
  connect as qzConnect,
  disconnect as qzDisconnect,
  subscribe as subscribeQz,
  type QZErrorCode,
  type QZState,
} from '@goshippo/print';

import { QZ_TRAY_CONNECT_OPTIONS } from '../constants/bootstrap.const';
import {
  type ShippoPrintConnectOptions,
  type ShippoPrintConnectReason,
  type ShippoPrintConnectionIntent,
  type ShippoPrintConnectionState,
} from '../types/shippoPrintConnection.type';

const DEFAULT_CONNECT_REASON: ShippoPrintConnectReason = 'manual';
const DEFAULT_RETRY_INTERVAL_MS = 1_000;
const AUTO_RECONNECT_INITIAL_RETRY_INTERVAL_MS = 1_000;
const AUTO_RECONNECT_MAX_RETRY_INTERVAL_MS = 60_000;
const AUTO_RECONNECT_RETRY_UNTIL = Number.POSITIVE_INFINITY;

const RECOVERABLE_RECONNECT_ERROR_CODES = new Set<QZErrorCode>([
  'dropped',
  'racing',
  'unknown',
  'unreachable',
]);

const NON_RECOVERABLE_RECONNECT_ERROR_CODES = new Set<QZErrorCode>([
  'alreadyOpen',
  'browserUnsupported',
  'cancelled',
  'handshakeFailed',
]);

const EMPTY_STATE: ShippoPrintConnectionState = {
  activeIntent: null,
  isEnsuringConnection: false,
};

// Module-level singleton: one connect intent and one retry timer per app.
// Multiple hook consumers must coordinate through the same QZ socket, not race their own.
let state: ShippoPrintConnectionState = EMPTY_STATE;
let timer: ReturnType<typeof setTimeout> | null = null;
let inFlightConnect: Promise<void> | null = null;
let disconnectGeneration = 0;
let unsubscribeQz: (() => void) | null = null;
let autoReconnectEnabled = false;
let hasConnectedInSession = false;
let wasConnected = false;

const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of listeners) listener();
};

const setState = (next: ShippoPrintConnectionState) => {
  if (state === next) return;
  state = next;
  emit();
};

const clearTimer = () => {
  if (timer === null) return;
  clearTimeout(timer);
  timer = null;
};

const resolveRetryUntil = (
  retryWindowMs: ShippoPrintConnectOptions['retryWindowMs'],
  now: number,
) =>
  typeof retryWindowMs === 'number' && Number.isFinite(retryWindowMs) && retryWindowMs > 0
    ? now + retryWindowMs
    : null;

const normalizeRetryIntervalMs = (retryIntervalMs: ShippoPrintConnectOptions['retryIntervalMs']) =>
  typeof retryIntervalMs === 'number' && Number.isFinite(retryIntervalMs) && retryIntervalMs > 0
    ? retryIntervalMs
    : DEFAULT_RETRY_INTERVAL_MS;

const clearIntent = () => {
  clearTimer();
  if (state.activeIntent === null && !state.isEnsuringConnection) return;
  setState(EMPTY_STATE);
};

const mergeIntent = (options: ShippoPrintConnectOptions = {}) => {
  const now = Date.now();
  const incomingRetryUntil = resolveRetryUntil(options.retryWindowMs, now);
  const current = state.activeIntent;

  const retryUntil =
    current?.retryUntil === null || current?.retryUntil === undefined
      ? incomingRetryUntil
      : incomingRetryUntil === null
      ? current.retryUntil
      : Math.max(current.retryUntil, incomingRetryUntil);

  const retryIntervalMs =
    options.retryIntervalMs === undefined
      ? current?.retryIntervalMs ?? DEFAULT_RETRY_INTERVAL_MS
      : normalizeRetryIntervalMs(options.retryIntervalMs);

  setState({
    activeIntent: {
      attemptCount: current?.attemptCount ?? 0,
      reason: options.reason ?? current?.reason ?? DEFAULT_CONNECT_REASON,
      retryIntervalMs,
      retryUntil,
      startedAt: current?.startedAt ?? now,
      updatedAt: now,
    },
    isEnsuringConnection: true,
  });
};

const incrementAttemptCount = () => {
  const { activeIntent } = state;
  if (activeIntent === null) return;

  setState({
    activeIntent: {
      ...activeIntent,
      attemptCount: activeIntent.attemptCount + 1,
      updatedAt: Date.now(),
    },
    isEnsuringConnection: true,
  });
};

const hasRetryTimeRemaining = (
  intent: ShippoPrintConnectionIntent,
): intent is { retryUntil: number } & ShippoPrintConnectionIntent =>
  intent.retryUntil !== null && intent.retryUntil > Date.now();

const isAutoReconnectIntent = (intent: ShippoPrintConnectionIntent) =>
  intent.reason === 'retry' && intent.retryUntil === AUTO_RECONNECT_RETRY_UNTIL;

const shouldRetryAfterFailure = (
  intent: ShippoPrintConnectionIntent,
  lastErrorCode: QZErrorCode | null,
) => {
  if (!isAutoReconnectIntent(intent)) return hasRetryTimeRemaining(intent);
  if (!autoReconnectEnabled) return false;
  if (lastErrorCode === null) return true;
  if (NON_RECOVERABLE_RECONNECT_ERROR_CODES.has(lastErrorCode)) return false;
  return RECOVERABLE_RECONNECT_ERROR_CODES.has(lastErrorCode);
};

const increaseAutoReconnectBackoff = () => {
  const { activeIntent } = state;
  if (activeIntent === null || !isAutoReconnectIntent(activeIntent)) return;

  setState({
    activeIntent: {
      ...activeIntent,
      retryIntervalMs: Math.min(
        activeIntent.retryIntervalMs * 2,
        AUTO_RECONNECT_MAX_RETRY_INTERVAL_MS,
      ),
      updatedAt: Date.now(),
    },
    isEnsuringConnection: true,
  });
};

const scheduleNextAttempt = () => {
  clearTimer();

  const { activeIntent } = state;
  if (activeIntent === null) return;

  if (getQzSnapshot().connected) {
    clearIntent();
    return;
  }

  if (!shouldRetryAfterFailure(activeIntent, getQzSnapshot().lastErrorCode)) {
    clearIntent();
    return;
  }

  if (activeIntent.retryUntil === null) {
    clearIntent();
    return;
  }

  const delay =
    activeIntent.retryUntil === AUTO_RECONNECT_RETRY_UNTIL
      ? activeIntent.retryIntervalMs
      : Math.min(activeIntent.retryIntervalMs, activeIntent.retryUntil - Date.now());
  timer = setTimeout(() => runConnectAttempt(false), delay);
};

const startAutoReconnect = () => {
  if (!autoReconnectEnabled || !hasConnectedInSession || inFlightConnect !== null) return;
  if (state.activeIntent !== null) return;

  const now = Date.now();
  setState({
    activeIntent: {
      attemptCount: 0,
      reason: 'retry',
      retryIntervalMs: AUTO_RECONNECT_INITIAL_RETRY_INTERVAL_MS,
      retryUntil: AUTO_RECONNECT_RETRY_UNTIL,
      startedAt: now,
      updatedAt: now,
    },
    isEnsuringConnection: true,
  });
  scheduleNextAttempt();
};

const handleQzStateChange = () => {
  const qzState = getQzSnapshot();
  const connected = qzState.connected;

  if (connected) {
    hasConnectedInSession = true;
    wasConnected = true;
    clearIntent();
    return;
  }

  if (wasConnected) {
    wasConnected = false;
    if (
      qzState.lastErrorCode === null ||
      RECOVERABLE_RECONNECT_ERROR_CODES.has(qzState.lastErrorCode)
    )
      startAutoReconnect();
  }
};

export const startShippoPrintConnectionManager = () => {
  if (unsubscribeQz !== null) return;
  unsubscribeQz = subscribeQz(handleQzStateChange);
  handleQzStateChange();
};

const runConnectAttempt = (allowSingleAttempt: boolean) => {
  clearTimer();

  const { activeIntent } = state;
  if (activeIntent === null) return;

  const qzState: QZState = getQzSnapshot();
  if (qzState.connected) {
    clearIntent();
    return;
  }

  if (!allowSingleAttempt && !hasRetryTimeRemaining(activeIntent)) {
    clearIntent();
    return;
  }

  if (inFlightConnect !== null) return;

  incrementAttemptCount();
  const generation = disconnectGeneration;

  try {
    const promise = qzConnect(QZ_TRAY_CONNECT_OPTIONS);
    inFlightConnect = promise;

    void promise
      .then(() => {
        if (generation !== disconnectGeneration) {
          void qzDisconnect().catch(() => {
            // The disconnect request is best-effort cleanup after a stale connect resolves.
          });
          return;
        }

        clearIntent();
      })
      .catch(() => {
        if (generation !== disconnectGeneration) return;
        increaseAutoReconnectBackoff();
        scheduleNextAttempt();
      })
      .finally(() => {
        if (inFlightConnect === promise) inFlightConnect = null;
      });
  } catch {
    scheduleNextAttempt();
  }
};

export const connectShippoPrint = (options: ShippoPrintConnectOptions = {}) => {
  startShippoPrintConnectionManager();

  if (getQzSnapshot().connected) {
    clearIntent();
    return;
  }

  mergeIntent(options);
  runConnectAttempt(true);
};

export const enableShippoPrintAutoReconnect = () => {
  startShippoPrintConnectionManager();
  autoReconnectEnabled = true;
};

export const disableShippoPrintAutoReconnect = () => {
  autoReconnectEnabled = false;
  if (state.activeIntent !== null && isAutoReconnectIntent(state.activeIntent)) clearIntent();
};

export const disconnectShippoPrint = () => {
  startShippoPrintConnectionManager();

  disconnectGeneration += 1;
  disableShippoPrintAutoReconnect();
  clearIntent();

  void qzDisconnect().catch(() => {
    // QZ disconnect is best-effort from this business-level control.
  });
};

export const subscribeShippoPrintConnection = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getShippoPrintConnectionSnapshot = () => state;

export const resetShippoPrintConnectionManagerForTests = () => {
  clearTimer();
  state = EMPTY_STATE;
  inFlightConnect = null;
  disconnectGeneration = 0;
  autoReconnectEnabled = false;
  hasConnectedInSession = false;
  wasConnected = false;
  listeners.clear();
  if (unsubscribeQz !== null) {
    unsubscribeQz();
    unsubscribeQz = null;
  }
};
